import React, { useState } from 'react'
import { Table, Button, Space, Modal, Tag, Tooltip, Popconfirm, Card, Alert, message } from 'antd'
import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons'
import { useOferte } from './hooks/useOferte'
import OfertaForm from './components/OfertaForm'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

function ClientOfferts() {
  const { client_mongo_id } = useParams()
  const { oferte, loading, pagination, fetchOferte, createOferta, updateOferta, deleteOferta, statusOptions } = useOferte(client_mongo_id)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingOferta, setEditingOferta] = useState(null)
  const [messageError, setMessageError] = useState(null)
  const navigate = useNavigate()

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(option => option.id === status)
    return statusOption ? statusOption.color : '#6B7280'
  }

  const getStatusText = (status) => {
    const statusOption = statusOptions.find(option => option.id === status)
    return statusOption ? statusOption.label : status
  }

  const columns = [
    {
      title: 'ID Ofertă',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id) => (
        <div style={{ 
          fontSize: '13px',
          fontWeight: '600',
          color: '#1890ff',
          backgroundColor: '#e6f7ff',
          padding: '4px 8px',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          #{id}
        </div>
      ),
    },
    {
      title: 'Ofertă',
      dataIndex: 'id',
      key: 'oferta',
      width: 160,
      render: (id, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Tooltip title="Vezi detalii ofertă">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              style={{
                borderRadius: '8px',
                fontWeight: '600',
                fontSize: '13px',
                height: '32px',
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              }}
              onClick={() => navigate(`/dashboard/oferte/${record.id}`)}
            >
              Oferta #{id}
            </Button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Produse & Status',
      key: 'products_status',
      width: 280,
      render: (_, record) => {
        const totalProducts = (record.produse?.length || 0) + (record.info?.new_products?.length || 0)
        const newProducts = record.info?.new_products?.length || 0
        
        return (
          <div style={{ fontSize: '12px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '8px',
              padding: '4px 8px',
              backgroundColor: '#f5f5f5',
              borderRadius: '4px'
            }}>
              <span style={{ fontWeight: '600' }}>Total Produse:</span>
              <span style={{ fontWeight: '600', color: '#1890ff' }}>{totalProducts}</span>
            </div>
            
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {newProducts > 0 && (
                <Tag color="red" style={{ fontSize: '11px', margin: '2px' }}>
                  🆕 {newProducts} noi
                </Tag>
              )}
              
              {record.produse?.length === 0 && newProducts === 0 && (
                <Tag color="default" style={{ fontSize: '11px', margin: '2px' }}>
                  Fără produse
                </Tag>
              )}
            </div>
          </div>
        )
      },
    },
    {
      title: 'Situație Financiară',
      key: 'financiar',
      width: 200,
      render: (_, record) => {
        const totalVanzare = record.produse?.reduce((sum, produs) => {
          return sum + (produs.sale_price * produs.product_qt)
        }, 0) || 0
        
        return (
          <Card 
            size="small" 
            style={{ 
              fontSize: '12px', 
              borderRadius: '8px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #91d5ff'
            }}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              padding: '8px 6px',
              backgroundColor: '#1890ff',
              color: 'white',
              borderRadius: '4px',
              fontWeight: '700',
              fontSize: '11px'
            }}>
              <span>💰 TOTAL VÂNZARE:</span>
              <span>{totalVanzare.toFixed(0)} MDL</span>
            </div>
          </Card>
        )
      },
    },
    {
      title: 'Status & Data',
      key: 'status_date',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <Tag 
            color={getStatusColor(record.status)} 
            style={{ marginBottom: '8px', fontWeight: '500' }}
          >
            {getStatusText(record.status)}
          </Tag>
          <div style={{ color: '#666' }}>
            📅 {new Date(record.date_added).toLocaleDateString('ro-RO')}
          </div>
        </div>
      ),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Șterge">
            <Popconfirm
              title="Ești sigur că vrei să ștergi această ofertă?"
              onConfirm={() => deleteOferta(record.id)}
              okText="Da"
              cancelText="Nu"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ]

  const handleTableChange = (paginationInfo) => {
    fetchOferte(paginationInfo.current, paginationInfo.pageSize, { client_mongo_id: client_mongo_id })
  }

  const handleFormSubmit = async (values) => {
    let success = false
    if (editingOferta) {
      success = await updateOferta(editingOferta.id, values)
    } else {
      // Adăugăm client_id la valorile ofertei noi
      success = await createOferta({ ...values, client_mongo_id: client_mongo_id })
    }

    if (success) {
      setModalVisible(false)
      setEditingOferta(null)
      message.success('Ofertă creată cu succes')
    } else {
      setMessageError('Eroare la crearea ofertei')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center p-8">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">Ofertele Clientului</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestionează ofertele pentru acest client
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          style={{ 
            borderRadius: '8px',
            height: '40px',
            fontWeight: '600',
            background: 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(82, 196, 26, 0.3)'
          }}
          onClick={() => {
            setEditingOferta(null)
            setModalVisible(true)
          }}
        >
          Ofertă Nouă
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={oferte}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) =>
            `Afișare ${range[0]}-${range[1]} din ${total} oferte`,
          pageSizeOptions: ['10', '20', '50'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
        size="middle"
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
          padding: '0 16px'
        }}
      />

      <Modal
        title={editingOferta ? 'Editează Oferta' : 'Adaugă Ofertă Nouă'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false)
          setEditingOferta(null)
        }}
        footer={null}
        width={800}
        destroyOnClose
      >
        <OfertaForm
          initialValues={editingOferta}
          onSubmit={handleFormSubmit}
          messageError={messageError}
          onCancel={() => {
            setModalVisible(false)
            setEditingOferta(null)
          }}
        />
      </Modal>
    </div>
  )
}

export default ClientOfferts